import type { DepartmentImpact } from "../../api/client";
import { useModalFocusTrap } from "./useModalFocusTrap";

interface DepartmentLifecycleDialogProps {
  action: "deactivate" | "reactivate";
  busy: boolean;
  impact: DepartmentImpact | null;
  name: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DepartmentLifecycleDialog({
  action,
  busy,
  impact,
  name,
  onCancel,
  onConfirm,
}: DepartmentLifecycleDialogProps) {
  const dialog = useModalFocusTrap<HTMLElement>("[data-initial-focus]");
  const blocked = action === "deactivate" && Boolean(impact?.active_user_count);
  return (
    <div className="confirmation-backdrop" role="presentation">
      <section
        ref={dialog}
        aria-labelledby="department-action-title"
        aria-modal="true"
        className="confirmation-dialog department-action-dialog"
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Escape" && !busy) onCancel();
        }}
        role="alertdialog"
        tabIndex={-1}
      >
        <h3 id="department-action-title">
          {action === "deactivate" ? `确认停用 ${name}` : `确认恢复 ${name}`}
        </h3>
        {action === "deactivate" && impact ? (
          <>
            <dl className="department-impact-grid">
              <div>
                <dt>活动用户</dt>
                <dd>{impact.active_user_count}</dd>
              </div>
              <div>
                <dt>活动授权</dt>
                <dd>{impact.active_grant_count}</dd>
              </div>
              <div>
                <dt>活动配额</dt>
                <dd>{impact.active_quota_count}</dd>
              </div>
            </dl>
            <p>
              {blocked
                ? "仍有活动用户，必须先迁移或停用这些账户。当前不会执行任何变更。"
                : "停用将撤销活动授权并停用活动配额；历史运行、用量和引用会继续保留。"}
            </p>
          </>
        ) : (
          <p>恢复后可用于新的归属和范围配置，但不会自动恢复此前撤销的授权或配额。</p>
        )}
        <div className="modal-actions">
          <button data-initial-focus disabled={busy} onClick={onCancel} type="button">
            {blocked ? "知道了" : "取消"}
          </button>
          {!blocked && (
            <button disabled={busy} onClick={onConfirm} type="button">
              {busy ? "正在处理…" : action === "deactivate" ? "确认停用" : "确认恢复"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
